export interface DonutSegmentInput {
  id: string;
  /** 0–100. Negative or NaN values collapse to 0; values are clamped collectively to ≤ 100. */
  percent: number;
  color: string;
}

export interface DonutSegmentPath {
  id: string;
  color: string;
  /** SVG `stroke-dasharray` attribute string. */
  strokeDasharray: string;
  /** SVG `stroke-dashoffset` attribute number. */
  strokeDashoffset: number;
}

export interface BuildSegmentPathsOptions {
  size: number;
  strokeWidth: number;
  segments: readonly DonutSegmentInput[];
}

/**
 * The floor for a segment that has something to show. Arc length is linear in
 * percent, so a tier holding a fraction of a percent of the scope resolves to a
 * sub-pixel stroke and reads as absent — on the sensitivity donut that is the
 * Highly Confidential tier, the one the widget exists to surface, while the
 * table beside it still lists the count. Six pixels is the smallest arc that
 * reads as a mark rather than an artefact.
 *
 * `FlowLayer` clamps its pipes the same way (MIN_PIPE_THICKNESS_PX). The cost is
 * the same too: arcs stop being exactly proportional at the bottom of the range.
 * That is the right trade for a chart whose smallest slice matters most.
 */
const MIN_SEGMENT_PX = 6;

/**
 * Pure function. Given a donut size + stroke width + ordered segment percents,
 * returns one path-attribute object per segment so the consumer can render
 * `<circle stroke-dasharray={…} stroke-dashoffset={…} />` declaratively.
 *
 * Total percent > 100 (rounding errors) is normalized so the rendered arcs
 * sum to ≤ circumference (no overflow). A zero-percent segment emits a
 * zero-length dash and does not render; anything above zero gets at least
 * `MIN_SEGMENT_PX`, provided the ring is big enough to give every positive
 * segment that much — each one reserves the floor for those still to come.
 */
export function buildSegmentPaths(options: BuildSegmentPathsOptions): readonly DonutSegmentPath[] {
  const { size, strokeWidth, segments } = options;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const sanitized = segments.map((s) => ({
    ...s,
    percent: Number.isFinite(s.percent) && s.percent > 0 ? s.percent : 0,
  }));

  const total = sanitized.reduce((sum, s) => sum + s.percent, 0);
  const scale = total > 100 ? 100 / total : 1;

  let accumulatedOffset = 0;
  const paths: DonutSegmentPath[] = [];

  // The floor is granted in ring order, so each segment has to leave room for
  // the ones behind it — otherwise a handful of floored slices at the front eat
  // the ring and a later segment with a real value renders sub-pixel or not at
  // all. On a ring too small to floor everything the floor is dropped entirely
  // rather than applied to whoever comes first: exact proportions are the
  // honest answer when no arrangement can satisfy the minimum.
  let positivesLeft = sanitized.filter((s) => s.percent > 0).length;
  const canFloor = positivesLeft * MIN_SEGMENT_PX <= circumference;

  for (const seg of sanitized) {
    const scaledPercent = seg.percent * scale;
    const exact = (scaledPercent / 100) * circumference;
    if (seg.percent > 0) positivesLeft -= 1;
    const remaining = Math.max(0, circumference - accumulatedOffset);
    const room = Math.max(0, remaining - (canFloor ? MIN_SEGMENT_PX * positivesLeft : 0));
    const wanted = canFloor ? Math.max(MIN_SEGMENT_PX, exact) : exact;
    const dashLength = seg.percent > 0 ? Math.min(wanted, room) : 0;
    const strokeDasharray = `${dashLength} ${circumference - dashLength}`;
    const strokeDashoffset = circumference - accumulatedOffset;

    paths.push({
      id: seg.id,
      color: seg.color,
      strokeDasharray,
      strokeDashoffset,
    });

    accumulatedOffset += dashLength;
  }

  return paths;
}
